import { DirectionalHint, FontIcon, mergeStyleSets, useTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPActionButton, IPCallout, IPStack, IPSearchBox } from 'baseComponents';
import { IPLink } from 'baseComponents/Link/IPLink';
import KPIDetailsForm from 'components/KPI/KPIDetailsForm';
import PreviewToggle from 'components/KPI/PreviewToggle';
import ReuseSuggestions from 'components/KPI/ReuseSuggestions';
import { SaveCallback, SaveDisabledCallback } from 'components/KPI/fieldPanelTypes';
import {
  addCreatedTags,
  applyPickType,
  useExpressionSaveGate,
  useKPIFieldUpdaters,
  usePreviewDebounce,
} from 'components/KPI/kpiDetailsForm.helpers';
import { globalStackStylesPanel, globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import Joi from 'joi';
import KPI, { KPITypes } from 'models/kpi/kpi';
import Tag from 'models/tag';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { findSimilarByName } from 'utils/nameSimilarity';

export interface IAddFieldDetailsProps {
  isOpen: boolean;
  onDismiss: () => void;
  onAddNewKPI: (item: KPI) => void | Promise<void | boolean>;
  isLoading: boolean;
  kpis: KPI[];
  formKpiIds?: number[];
  updatePreviewKPI: (kpi: KPI) => void;
  defaultIsField?: boolean;
  onIsFieldChange?: (isField: boolean) => void;
  onSaveCallback?: (callback: SaveCallback) => void;
  onSaveDisabledCallback?: (callback: SaveDisabledCallback) => void;
  candidates?: KPI[];
  onReuse?: (kpi: KPI) => void;
  onCreateNew?: (name: string) => void;
  defaultName?: string;
  hideReuse?: boolean;
  previewDismissed?: boolean;
  onTogglePreview?: () => void;
  previewToggleId?: string;
  compactLayout?: boolean;
}

export const AddFieldDetails = (props: IAddFieldDetailsProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'kpis', 'forms']);
  const entityTerm = (isField: boolean): string =>
    t(props.defaultIsField || isField ? 'kpis:Entity.Field' : 'kpis:Entity.KPI');
  const theme = useTheme();
  const [error, setError] = useState<Joi.ValidationError | undefined>(undefined);
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [showReuseKPI, setShowReuseKPI] = useState<boolean>(false);
  const [reuseReopenSignal, setReuseReopenSignal] = useState<number>(0);
  const [reuseSearch, setReuseSearch] = useState<string>('');
  const reuseAnchorRef = useRef<HTMLDivElement>(null);
  const nameFieldRef = useRef<HTMLDivElement>(null);
  const nameCheckSeq = useRef(0);
  const nameCheckTimer = useRef<number | undefined>(undefined);
  const [kpiToAdd, setKPIToAdd] = useState<KPI>(() => {
    const seed = new KPI();
    seed.isField = props.defaultIsField ?? false;
    seed.name = props.defaultName ?? '';

    return seed;
  });
  // Pristine baseline for the hasChanged comparison. useRef captures the initial draft once (no
  // per-render allocation); the open-reset effect re-baselines it, and the draft is only ever
  // replaced via setKPIToAdd clones, so this reference stays pristine.
  const originalRef = useRef<KPI>(kpiToAdd);
  const [kpiTags, setKPITags] = useState<Tag[]>([]);

  const fieldUpdaters = useKPIFieldUpdaters({
    kpi: kpiToAdd,
    onChange: setKPIToAdd,
    tags: kpiTags,
    onTagsChange: setKPITags,
  });

  usePreviewDebounce(kpiToAdd, props.isOpen, props.isLoading, props.updatePreviewKPI);

  const expressionGate = useExpressionSaveGate(kpiToAdd, originalRef.current);

  useEffect(() => {
    if (props.isOpen) {
      setNameError(undefined);
      setError(undefined);
      expressionGate.reset();
      const seed = new KPI();
      seed.isField = props.defaultIsField ?? false;
      seed.name = props.defaultName ?? '';
      originalRef.current = seed.clone();
      setKPIToAdd(seed.clone());
      setKPITags([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.defaultIsField, props.defaultName, props.isOpen]);

  useEffect(() => {
    if (props.onSaveCallback) props.onSaveCallback(onSave);
    if (props.onSaveDisabledCallback) props.onSaveDisabledCallback(disableSave);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kpiToAdd, kpiTags, error, nameError, expressionGate.state]);

  const _localizedFields: Record<string, string> = {
    name: t('kpis:Fields.Name'),
    defValue: t('kpis:Fields.Question'),
  };

  const getFieldError = (fieldName: string): string | undefined => {
    if (nameError) return nameError;

    return error?.details?.find((e) => e.context?.key === fieldName)?.message;
  };

  const validate = async (fieldName: string, nextKpi: KPI): Promise<string> => {
    const result = nextKpi.validate(_localizedFields);
    setError(result.error);
    if (result.error) {
      const fieldError = result.error.details.find((e) => e.context?.key === fieldName);
      if (fieldError) return fieldError.message;
    }

    if (fieldName === 'name') {
      // Newest check wins: cancel the pending server check and invalidate in-flight responses,
      // so a stale "no duplicate" can never clear a real error.
      if (nameCheckTimer.current) window.clearTimeout(nameCheckTimer.current);
      const seq = ++nameCheckSeq.current;
      const typed = nextKpi.name.trim().toLowerCase();

      const applyExistingVerdict = (existingId: number) => {
        const onThisForm = props.candidates !== undefined && !props.candidates.some((c) => c.kpiId === existingId);
        setNameError(
          onThisForm
            ? t('kpis:Errors.NameErrorOnForm', { entity: entityTerm(nextKpi.isField) })
            : t('kpis:Errors.NameError', { entity: entityTerm(nextKpi.isField) }),
        );
      };

      if (!typed) {
        setNameError(undefined);

        return '';
      }

      // Instant verdict from the loaded pool; no roundtrip for the common case.
      const localMatch = props.kpis.find((k) => k.name.trim().toLowerCase() === typed);
      if (localMatch) {
        applyExistingVerdict(localMatch.kpiId);

        return '';
      }

      // The server is only asked about names outside the loaded pool (hidden or restricted),
      // once per typing pause instead of once per keystroke.
      nameCheckTimer.current = window.setTimeout(async () => {
        try {
          const existingId = await KPI.nameExists(nextKpi.name.toLowerCase(), appContext);
          if (seq !== nameCheckSeq.current) return;

          // The server matches more loosely than the local compare (collation), so a hit can
          // still be in the loaded pool; only a truly invisible name gets the hidden message.
          if (existingId && props.kpis.some((k) => k.kpiId === existingId)) {
            applyExistingVerdict(existingId);
          } else if (existingId) {
            setNameError(t('kpis:Errors.NameErrorHiddenKPI', { entity: entityTerm(nextKpi.isField) }));
          } else {
            setNameError(undefined);
          }
        } catch {
          // A failed check keeps the current verdict; the server still rejects real duplicates.
        }
      }, 400);
    }

    return '';
  };

  const hasChanged = (): boolean => !kpiToAdd.isEqual(originalRef.current);

  // The dirty-check alone must never enable Save: editing any non-name field would otherwise
  // allow posting a draft whose required name is still empty (the API rejects it with 400
  // Trans[0].Name). Joi runs against the current draft on every gate evaluation, like the
  // other create panels (AddObjectivePanel, AddProcessPanel).
  const hasError = (): boolean => !!kpiToAdd.validate(_localizedFields).error;

  const disableSave = (): boolean =>
    !hasChanged() || hasError() || !!error || !!nameError || expressionGate.blocksSave || props.isLoading;

  const onSave = async () => {
    const validationResult = kpiToAdd.validate(_localizedFields);
    if (validationResult.error) {
      setError(validationResult.error);

      return false;
    }
    const createdTags = await addCreatedTags(kpiTags, appContext);
    const allTags = kpiTags.filter((a) => a.tagId >= 0);
    allTags.push(...createdTags);
    kpiToAdd.tagIds = allTags.map((tag) => tag.tagId);

    return await props.onAddNewKPI(kpiToAdd);
  };

  const reuseStyles = mergeStyleSets({
    reuseList: { maxHeight: 240, overflowY: 'auto', marginTop: 8 },
    reuseRow: {
      display: 'block',
      width: '100%',
      textAlign: 'left',
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      padding: '8px 10px',
      borderRadius: 4,
      fontSize: 14,
      fontFamily: 'inherit',
      color: theme.palette.neutralPrimary,
      selectors: { ':hover': { background: theme.palette.neutralLighter } },
    },
    reuseCreateRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      width: '100%',
      textAlign: 'left',
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      padding: '8px 10px',
      borderRadius: 4,
      fontSize: 14,
      fontWeight: 600,
      fontFamily: 'inherit',
      color: theme.palette.themePrimary,
      selectors: { ':hover': { background: theme.palette.neutralLighter } },
    },
    dupError: { marginTop: 5, fontSize: 12, color: theme.semanticColors.errorText },
  });

  // The browse list offers KPIs only; fields are found via the typed-name suggestions instead
  // of being browsed (nobody scans a catalog of every field in the organization).
  const reuseMatches = (props.candidates ?? []).filter(
    (k) => !k.isField && k.name.toLowerCase().includes(reuseSearch.trim().toLowerCase()),
  );

  const dupNameLower = kpiToAdd.name.trim().toLowerCase();
  // Count from the SAME match set the suggestions callout renders, so the "show existing"
  // link never advertises matches the callout cannot show (e.g. names under the similarity
  // matcher's minimum query length).
  const nameMatchCount =
    !props.hideReuse && props.onReuse && dupNameLower
      ? findSimilarByName(kpiToAdd.name, props.candidates ?? [], (k) => k.name, 4).length
      : 0;
  const showInteractiveDup = !!nameError && nameMatchCount > 0;
  const joiNameError = error?.details?.find((e) => e.context?.key === 'name')?.message;
  // '' (not undefined) while the interactive dup shows: KPIDetailsForm falls back to
  // fieldError('name') on undefined, which would stack the plain error under this one.
  const nameErrorMessage = showInteractiveDup ? joiNameError ?? '' : getFieldError('name');
  const entityTermPlural = (isField: boolean): string => (isField ? t('kpis:Entity.Fields') : t('kpis:Entity.KPIs'));

  const nameInfoMessage =
    !props.hideReuse && props.candidates && props.onReuse
      ? t('kpis:Fields.NameInfoReuse', { entity: entityTerm(kpiToAdd.isField) })
      : t('kpis:Fields.NameInfo', { entity: entityTerm(kpiToAdd.isField) });

  const renderReuseAfterName = () => (
    <>
      {showInteractiveDup && (
        <div role="alert" className={reuseStyles.dupError}>
          <div>{t('kpis:Errors.NameErrorExists', { entity: entityTerm(kpiToAdd.isField) })}</div>
          {/* A plainly labeled action link (default link color) instead of a link hidden inside the error text. */}
          <IPLink onClick={() => setReuseReopenSignal((s) => s + 1)}>
            {t('kpis:Errors.ShowExisting', { entities: entityTermPlural(kpiToAdd.isField), count: nameMatchCount })}
          </IPLink>
        </div>
      )}
      {!props.hideReuse && props.candidates && props.onReuse && (
        <ReuseSuggestions
          name={kpiToAdd.name}
          candidates={props.candidates}
          onReuse={props.onReuse}
          target={nameFieldRef}
          isOpen={props.isOpen}
          reopenSignal={reuseReopenSignal}
        />
      )}
      {!props.hideReuse && props.candidates && props.candidates.length > 0 && props.onReuse && (
        <div ref={reuseAnchorRef} style={{ marginTop: 4, display: 'inline-block' }}>
          <IPActionButton
            iconProps={{ iconName: 'Add' }}
            text={t('kpis:Reuse.ReuseKPILabel')}
            onClick={() => setShowReuseKPI(true)}
          />
        </div>
      )}
      {showReuseKPI && props.onReuse && (
        <IPCallout
          target={reuseAnchorRef}
          directionalHint={DirectionalHint.bottomLeftEdge}
          isBeakVisible={false}
          setInitialFocus
          onDismiss={() => {
            setShowReuseKPI(false);
            setReuseSearch('');
          }}
          styles={{
            root: {
              padding: 8,
              background: theme.semanticColors.bodyBackground,
              border: `1px solid ${theme.palette.neutralLight}`,
              borderRadius: 2,
              boxShadow: theme.effects.elevation8,
            },
          }}
        >
          <div style={{ width: 280 }}>
            <IPSearchBox
              placeholder={t('kpis:Reuse.ReuseKPIPlaceholder')}
              value={reuseSearch}
              onChange={(_, v) => setReuseSearch(v ?? '')}
            />
            <div className={reuseStyles.reuseList}>
              {reuseMatches.map((k) => (
                <button
                  key={k.kpiId}
                  type="button"
                  className={reuseStyles.reuseRow}
                  onClick={() => {
                    props.onReuse?.(k);
                    setShowReuseKPI(false);
                    setReuseSearch('');
                  }}
                >
                  {k.name}
                </button>
              ))}
              {!reuseMatches.some((k) => k.name.toLowerCase() === reuseSearch.trim().toLowerCase()) && (
                <button
                  type="button"
                  className={reuseStyles.reuseCreateRow}
                  onClick={() => {
                    const newName = reuseSearch.trim();
                    setShowReuseKPI(false);
                    setReuseSearch('');
                    if (props.onCreateNew) {
                      props.onCreateNew(newName);

                      return;
                    }
                    if (newName) {
                      const newKPI = kpiToAdd.clone();
                      newKPI.name = newName;
                      validate('name', newKPI);
                      setKPIToAdd(newKPI);
                    }
                  }}
                >
                  <FontIcon iconName="Add" aria-hidden={true} />
                  {reuseSearch.trim()
                    ? t('kpis:Reuse.CreateNewKPINamed', { name: reuseSearch.trim() })
                    : t('kpis:Reuse.CreateNewKPI')}
                </button>
              )}
            </div>
          </div>
        </IPCallout>
      )}
    </>
  );

  return (
    <IPStack
      id="kpiPreviewTarget"
      verticalFill={!props.compactLayout}
      styles={props.compactLayout ? { root: { paddingTop: 0, paddingBottom: 0 } } : globalStackStylesPanel}
      tokens={props.compactLayout ? globalStackTokensGapSmall : globalStackTokensGapMedium}
    >
      {props.onTogglePreview && (
        <PreviewToggle
          previewDismissed={props.previewDismissed}
          onTogglePreview={props.onTogglePreview}
          previewToggleId={props.previewToggleId}
        />
      )}
      <KPIDetailsForm
        kpi={kpiToAdd}
        kpis={props.kpis}
        formKpiIds={props.formKpiIds}
        originalExpression={expressionGate.originalExpression}
        expressionValidationUpdated={expressionGate.onValidationStateChange}
        mode="add"
        entityTerm={entityTerm}
        onChange={setKPIToAdd}
        restrictTypeChanges={false}
        onPickType={(type) => {
          const next = applyPickType(kpiToAdd, type, t);
          expressionGate.reset();
          setKPIToAdd(next);
          if (type === KPITypes.Expression) {
            props.onIsFieldChange?.(false);
          }
        }}
        onIsFieldChange={props.onIsFieldChange}
        {...fieldUpdaters}
        tags={kpiTags}
        nameInfoMessage={nameInfoMessage}
        nameErrorMessage={nameErrorMessage}
        nameFieldErrorBorder={showInteractiveDup}
        fieldError={getFieldError}
        onNameChange={(nextKpi) => {
          validate('name', nextKpi);
          setKPIToAdd(nextKpi);
        }}
        nameFieldRef={nameFieldRef}
        showTypeConfig={true}
        showInstruction={true}
        showExpectedResult={true}
        showAttachments={true}
        showPermissions={true}
        compactLayout={props.compactLayout}
        renderAfterName={renderReuseAfterName}
      />
    </IPStack>
  );
};
